import { z } from "zod/v4";

import { ExperienceCategoryList } from "../../../dtos/experience-categories";
import {
  ExperienceStatusList,
  ExperienceTypeList,
} from "../../../dtos/experiences";

import { AuditRecommendationStateList } from "./recommendation-lifecycle";

const MAX_PER_QUERY = 200;

/**
 * A body is never parsed here: node-backend stores and returns it as it came,
 * so a card written under an older or newer schema can never fail a route.
 */
const BodySchema = z.unknown();

export const AuditRecommendationInputSchema = z.object({
  /** The card's lasting id, made by web/ai when the walk builds it. */
  id: z.uuid(),
  schemaVersion: z.number().int().min(0),
  body: BodySchema,
  sessionId: z.string().max(64).optional(),
  /** The user the card was shown to. */
  userId: z.string().min(1).max(64),
  /** When the card was shown. Defaults to now. */
  shownAtTs: z.number().int().positive().optional(),
});

export const AuditRecommendationExperienceSchema = z.object({
  id: z.string(),
  category: z.enum(ExperienceCategoryList),
  type: z.enum(ExperienceTypeList),
  status: z.enum(ExperienceStatusList),
  archivedAtTs: z.number().nullable(),
});

export const AuditRecommendationStatusSchema = z.object({
  id: z.string(),
  state: z.enum(AuditRecommendationStateList),
  /** When it last moved; null while it has never moved. */
  stateAtTs: z.number().nullable(),
  stateByUserId: z.string().nullable(),
  /** The test built from it while that test exists; archived still counts. */
  experience: AuditRecommendationExperienceSchema.nullable(),
  /** When it was shown. Optional because a server from before it sends none. */
  createdAtTs: z.number().optional(),
});

export const AuditRecommendationDtoSchema =
  AuditRecommendationStatusSchema.extend({
    schemaVersion: z.number(),
    /** When it was shown. */
    createdAtTs: z.number(),
    sessionId: z.string().nullable(),
    createdByUserId: z.string(),
    body: BodySchema.optional(),
  });

export const SaveAuditRecommendationsRequestBodySchema = z.object({
  recommendations: z.array(AuditRecommendationInputSchema).min(1).max(10),
});

export const SaveAuditRecommendationsResponseBodySchema = z.object({
  /** In request order, with bodies. */
  recommendations: z.array(AuditRecommendationDtoSchema),
});

export const QueryAuditRecommendationsRequestBodySchema = z.object({
  ids: z.array(z.string().min(1)).max(MAX_PER_QUERY).optional(),
  state: z.enum(AuditRecommendationStateList).optional(),
  experienceId: z.string().min(1).optional(),
  /** Bounds on createdAtTs, both inclusive. */
  createdFrom: z.number().int().optional(),
  createdTo: z.number().int().optional(),
  limit: z.number().int().min(1).max(MAX_PER_QUERY).optional(),
  cursor: z.string().min(1).optional(),
  withBody: z.boolean().optional(),
});

export const QueryAuditRecommendationsResponseBodySchema = z.object({
  /** Newest first: by stateAtTs when a state is asked for, else by createdAtTs. */
  recommendations: z.array(AuditRecommendationDtoSchema),
  /** Set while more rows match. */
  next: z.string().nullable(),
});

/** Where in the app a merchant acted on a recommendation, for analytics. */
export const AuditRecommendationLocationSchema = z.enum([
  "audit_panel",
  "chat",
  "saved_ideas",
]);

export const SetAuditRecommendationStateRequestBodySchema = z.object({
  recommendationId: z.string().min(1).max(255),
  state: z.enum(AuditRecommendationStateList),
  /** Where the merchant changed it. Only analytics reads it. */
  location: AuditRecommendationLocationSchema.optional(),
});

export const SetAuditRecommendationStateResponseBodySchema = z.object({
  status: AuditRecommendationStatusSchema,
});

/**
 * web/ai's route that keeps cards a conversation showed but node-backend has
 * no row for, read from the conversation itself.
 */
export const SaveAuditRecommendationsFromConversationRequestBodySchema =
  z.object({
    sessionId: z.string().min(1),
    recommendationIds: z.array(z.string().min(1)).min(1).max(20),
  });

export const SaveAuditRecommendationsFromConversationResponseBodySchema =
  z.object({
    /** The statuses of the cards now saved; a card that could not be saved is left out. */
    recommendations: z.array(AuditRecommendationStatusSchema),
  });

export type AuditRecommendationInput = z.infer<
  typeof AuditRecommendationInputSchema
>;
export type AuditRecommendationExperience = z.infer<
  typeof AuditRecommendationExperienceSchema
>;
export type AuditRecommendationStatus = z.infer<
  typeof AuditRecommendationStatusSchema
>;
export type AuditRecommendationDto = z.infer<
  typeof AuditRecommendationDtoSchema
>;
export type SaveAuditRecommendationsRequestBodyType = z.infer<
  typeof SaveAuditRecommendationsRequestBodySchema
>;
export type SaveAuditRecommendationsResponseBodyType = z.infer<
  typeof SaveAuditRecommendationsResponseBodySchema
>;
export type QueryAuditRecommendationsRequestBodyType = z.infer<
  typeof QueryAuditRecommendationsRequestBodySchema
>;
export type QueryAuditRecommendationsResponseBodyType = z.infer<
  typeof QueryAuditRecommendationsResponseBodySchema
>;
export type AuditRecommendationLocation = z.infer<
  typeof AuditRecommendationLocationSchema
>;
export type SetAuditRecommendationStateRequestBodyType = z.infer<
  typeof SetAuditRecommendationStateRequestBodySchema
>;
export type SetAuditRecommendationStateResponseBodyType = z.infer<
  typeof SetAuditRecommendationStateResponseBodySchema
>;
export type SaveAuditRecommendationsFromConversationRequestBodyType = z.infer<
  typeof SaveAuditRecommendationsFromConversationRequestBodySchema
>;
export type SaveAuditRecommendationsFromConversationResponseBodyType = z.infer<
  typeof SaveAuditRecommendationsFromConversationResponseBodySchema
>;
